import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useUserStore, roleHome } from './store/userStore';
import { InstallBanner } from './components/InstallBanner';
import { AppLayout } from './components/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SiteGate } from './components/SiteGate';

import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/patient/Dashboard';
import Track from './pages/patient/Track';
import Insights from './pages/patient/Insights';
import Book from './pages/patient/Book';
import Profile from './pages/patient/Profile';
import DomainQuestionnaire from './pages/DomainQuestionnaire';
import DomainHistory from './pages/DomainHistory';
import HealthAssessment from './pages/HealthAssessment';
import AssessmentResults from './pages/AssessmentResults';
import BiomarkerInput from './pages/BiomarkerInput';
import BiomarkerAnalysis from './pages/BiomarkerAnalysis';
import WeightTracker from './pages/WeightTracker';
import PremiumUpgrade from './pages/PremiumUpgrade';
import ClinicianDashboard from './pages/clinician/ClinicianDashboard';
import PatientSummary from './pages/clinician/PatientSummary';
import PatientRecord from './pages/clinician/PatientRecord';
import ClinicianSchedule from './pages/clinician/ClinicianSchedule';
import SiteMap from './pages/SiteMap';
import Admin from './pages/Admin';
import Superadmin from './pages/Superadmin';

function RequireAuth({ roles, children }) {
  const user = useUserStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role || 'patient';
  if (roles && !roles.includes(role)) return <Navigate to={roleHome(role)} replace />;
  return children;
}

function Root() {
  const user = useUserStore((s) => s.user);
  if (user) return <Navigate to={roleHome(user.role || 'patient')} replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors offset={16} />
        <SiteGate>
        <InstallBanner />
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login />} />
          <Route path="/site-map" element={<SiteMap />} />
          <Route path="/onboarding" element={<Onboarding />} />

          {/* Patient app */}
          <Route
            path="/app"
            element={
              <RequireAuth roles={['patient']}>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="track" element={<Track />} />
            <Route path="insights" element={<Insights />} />
            <Route path="book" element={<Book />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Patient full-screen flows */}
          <Route
            path="/questionnaire/:domain"
            element={
              <RequireAuth roles={['patient']}>
                <DomainQuestionnaire />
              </RequireAuth>
            }
          />
          <Route
            path="/domain-history/:domain"
            element={
              <RequireAuth roles={['patient']}>
                <DomainHistory />
              </RequireAuth>
            }
          />
          <Route
            path="/assessment"
            element={
              <RequireAuth roles={['patient']}>
                <HealthAssessment />
              </RequireAuth>
            }
          />
          <Route
            path="/assessment/results"
            element={
              <RequireAuth roles={['patient']}>
                <AssessmentResults />
              </RequireAuth>
            }
          />
          <Route
            path="/biomarkers"
            element={
              <RequireAuth roles={['patient']}>
                <BiomarkerInput />
              </RequireAuth>
            }
          />
          <Route
            path="/biomarkers/analysis"
            element={
              <RequireAuth roles={['patient']}>
                <BiomarkerAnalysis />
              </RequireAuth>
            }
          />
          <Route
            path="/weight"
            element={
              <RequireAuth roles={['patient']}>
                <WeightTracker />
              </RequireAuth>
            }
          />
          <Route
            path="/upgrade"
            element={
              <RequireAuth roles={['patient']}>
                <PremiumUpgrade />
              </RequireAuth>
            }
          />

          {/* Clinician */}
          <Route
            path="/clinician"
            element={
              <RequireAuth roles={['clinician']}>
                <ClinicianDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/clinician/schedule"
            element={
              <RequireAuth roles={['clinician']}>
                <ClinicianSchedule />
              </RequireAuth>
            }
          />
          <Route
            path="/clinician/patient/:patientId"
            element={
              <RequireAuth roles={['clinician']}>
                <PatientSummary />
              </RequireAuth>
            }
          />
          <Route
            path="/clinician/patient/:patientId/record"
            element={
              <RequireAuth roles={['clinician']}>
                <PatientRecord />
              </RequireAuth>
            }
          />

          {/* Admin / Superadmin */}
          <Route
            path="/admin"
            element={
              <RequireAuth roles={['admin', 'superadmin']}>
                <Admin />
              </RequireAuth>
            }
          />
          <Route
            path="/superadmin"
            element={
              <RequireAuth roles={['superadmin']}>
                <Superadmin />
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
        </SiteGate>
      </BrowserRouter>
    </div>
  );
}
